import { useSyncExternalStore } from 'react';
import { doc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { ulid } from 'ulid';
import { getDb, getStorageClient } from '@talk2nick/shared';

import { extensionFor, mediaGlyph, type PreparedMedia } from './media';
import { expiresAtField } from './messages';

/**
 * 미디어 업로드 (PLAN 5-5장).
 *
 * 순서는 **문서 먼저, 업로드 나중**이다.
 *  1) `messages/{id}` 문서를 먼저 쓴다 — Firestore 로컬 캐시가 즉시 반영하므로
 *     버블이 바로 뜨고, 오프라인이어도 오프라인 큐에 남는다.
 *     `media.path`는 빈 문자열로 두어 "아직 없음"을 명시한다.
 *  2) Storage에 올리며 **진행률은 Firestore가 아니라 로컬 스토어에** 기록한다.
 *     진행률 쓰기는 규칙 위반(Firestore write 비용)이고, 1초마다 초 단위로
 *     바뀌는 값을 서버에 보낼 이유가 없다 (PLAN 5-8-5 낙관 업데이트).
 *  3) 업로드가 끝나면 `media.path`/`url`만 한 번 패치한다.
 *
 * 실패하면 문서는 그대로 두고 스토어에 'failed'를 남긴다 — 버블이 `!`를
 * 보여주고 탭하면 같은 Blob으로 재시도한다 (PLAN 5-5장 업로드 UX).
 */

/** 목록 미리보기 길이 상한 (text 전송과 동일한 규칙) */
const PREVIEW_MAX = 120;

export interface MediaUploadState {
  /** 0~1 */
  progress: number;
  status: 'uploading' | 'failed';
  /** 전송 전 object URL — 메모리 전용이라 Firestore에 절대 쓰지 않는다 */
  previewUrl?: string;
  /** 업로드 실패 상세 원인 (디버깅 / 사용자 툴팁용) */
  errorMessage?: string;
}

const listeners = new Set<() => void>();
let snapshot = new Map<string, MediaUploadState>();
/** 재시도를 위해 전처리 결과(Blob)를 메모리에 보관한다 */
const drafts = new Map<string, PreparedMedia>();

function emit(): void {
  for (const listener of listeners) listener();
}

function setUpload(messageId: string, next: MediaUploadState | null): void {
  const merged = new Map(snapshot);
  if (next) merged.set(messageId, next);
  else merged.delete(messageId);
  snapshot = merged;
  emit();
}

export function getMediaUpload(messageId: string): MediaUploadState | undefined {
  return snapshot.get(messageId);
}

/** 대화방 버블 목록 전체의 업로드 상태 — 리스너 1개를 버블들이 나눠 쓴다 */
export function useMediaUploads(): ReadonlyMap<string, MediaUploadState> {
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
}

/**
 * 메시지 하나의 업로드 상태만 구독한다.
 * 버블이 수십 개여도 스토어 전체를 구독하지 않도록 셀렉터를 좁힌 훅을 쓴다.
 */
export function useMediaUpload(messageId: string): MediaUploadState | undefined {
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const get = () => snapshot.get(messageId);
  return useSyncExternalStore(subscribe, get, get);
}

function revokePreview(messageId: string): void {
  const url = snapshot.get(messageId)?.previewUrl;
  if (url) URL.revokeObjectURL(url);
}

function storagePath(uid: string, convoId: string, messageId: string, name: string): string {
  return `users/${uid}/convos/${convoId}/${messageId}/${name}`;
}

async function upload(
  path: string,
  blob: Blob,
  contentType: string,
  onProgress: (ratio: number) => void,
): Promise<string> {
  const [{ getDownloadURL, ref: storageRef, uploadBytesResumable }, storage] = await Promise.all([
    import('firebase/storage'),
    getStorageClient(),
  ]);

  return new Promise<string>((resolve, reject) => {
    const task = uploadBytesResumable(storageRef(storage, path), blob, {
      contentType,
    });
    task.on(
      'state_changed',
      (snapshot) => {
        const ratio = snapshot.totalBytes > 0 ? snapshot.bytesTransferred / snapshot.totalBytes : 0;
        onProgress(ratio);
      },
      reject,
      async () => {
        try {
          resolve(await getDownloadURL(task.snapshot.ref));
        } catch (error) {
          reject(error);
        }
      },
    );
  });
}

export interface SendMediaExtras {
  replyTo?: string;
  forwardedFrom?: string;
}

/** 재시도에 필요한 문맥 — 메시지 ID만 알면 버블에서 탭 재시도가 가능하도록 보관한다 */
const contexts = new Map<string, { convoId: string; senderId: string }>();

/**
 * 미디어 메시지 전송.
 *
 * Firestore 문서를 먼저 만들기 때문에 resolve는 업로드 완료를 **기다리지 않는다** —
 * 버블은 이미 화면에 있고, 진행률은 스토어에서 갱신된다.
 */
export async function sendMedia(
  convoId: string,
  senderId: string,
  prepared: PreparedMedia,
  extras: SendMediaExtras = {},
): Promise<string> {
  const db = getDb();
  const messageId = ulid();
  const previewUrl = URL.createObjectURL(prepared.blob);

  drafts.set(messageId, prepared);
  contexts.set(messageId, { convoId, senderId });
  setUpload(messageId, { progress: 0, status: 'uploading', previewUrl });

  const messageRef = doc(db, 'convos', convoId, 'messages', messageId);
  await setDoc(messageRef, {
    type: prepared.kind,
    senderId,
    // path는 업로드 완료 전에는 비어 있다 — "아직 없음"을 명시적으로 표현한다
    media: {
      path: '',
      mime: prepared.mime,
      size: prepared.size,
      ...(prepared.fileName ? { fileName: prepared.fileName } : {}),
      ...(prepared.width !== undefined ? { width: prepared.width } : {}),
      ...(prepared.height !== undefined ? { height: prepared.height } : {}),
      ...(prepared.durationMs !== undefined ? { durationMs: prepared.durationMs } : {}),
    },
    reactions: {},
    reactionCount: 0,
    clientCreatedAt: Date.now(),
    sourceDevice: 'web',
    createdAt: serverTimestamp(),
    ...expiresAtField(convoId),
    ...extras,
  });

  // 목록 미리보기는 비정규화 필드다 — 실패해도 메시지 자체는 이미 전송됐다
  void updateDoc(doc(db, 'convos', convoId), {
    lastMessage: {
      text: (prepared.fileName ?? mediaGlyph(prepared.kind)).slice(0, PREVIEW_MAX),
      senderId,
      type: prepared.kind,
      messageId,
      createdAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  }).catch((error: unknown) => {
    console.error('[talk2nIcK] 목록 미리보기 갱신 실패:', error);
  });

  void runUpload(convoId, senderId, messageId, prepared).catch((error: unknown) => {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('[talk2nIcK] 미디어 업로드 실패:', error);
    setUpload(messageId, {
      ...(snapshot.get(messageId) ?? { progress: 0 }),
      status: 'failed',
      errorMessage: errorMsg,
    });
  });

  return messageId;
}

async function runUpload(
  convoId: string,
  senderId: string,
  messageId: string,
  prepared: PreparedMedia,
): Promise<void> {
  const ext = extensionFor(prepared.mime, prepared.kind);
  const name = `${messageId}.${ext}`;
  const path = storagePath(senderId, convoId, messageId, name);

  const url = await upload(path, prepared.blob, prepared.mime, (progress) => {
    const current = snapshot.get(messageId);
    if (current?.status === 'failed') return;
    setUpload(messageId, { progress, status: 'uploading', previewUrl: current?.previewUrl });
  });

  // Firestore의 media는 객체이므로 patch는 통째로 갈아끼운다 — 생성 때 넣은
  // fileName/width/height/durationMs를 그대로 다시 실어 보내야 한다.
  const media: Record<string, unknown> = {
    path,
    url,
    mime: prepared.mime,
    size: prepared.size,
    ...(prepared.fileName ? { fileName: prepared.fileName } : {}),
    ...(prepared.width !== undefined ? { width: prepared.width } : {}),
    ...(prepared.height !== undefined ? { height: prepared.height } : {}),
    ...(prepared.durationMs !== undefined ? { durationMs: prepared.durationMs } : {}),
  };

  if (prepared.thumbnail) {
    const thumbExt = extensionFor(prepared.thumbnail.blob.type, prepared.kind);
    const thumbPath = storagePath(senderId, convoId, messageId, `${messageId}_thumb.${thumbExt}`);
    media.thumbnailPath = thumbPath;
    media.thumbnailUrl = await upload(
      thumbPath,
      prepared.thumbnail.blob,
      prepared.thumbnail.blob.type,
      () => undefined,
    );
  }

  await updateDoc(doc(getDb(), 'convos', convoId, 'messages', messageId), { media });

  drafts.delete(messageId);
  contexts.delete(messageId);
  revokePreview(messageId);
  setUpload(messageId, null);
}

/** 실패한 업로드 재시도 — 같은 Blob을 그대로 다시 올린다 */
export function canRetryMediaUpload(messageId: string): boolean {
  return drafts.has(messageId) && contexts.has(messageId);
}

export async function retryMediaUpload(messageId: string): Promise<void> {
  const prepared = drafts.get(messageId);
  const context = contexts.get(messageId);
  if (!prepared || !context) return;
  setUpload(messageId, {
    progress: 0,
    status: 'uploading',
    previewUrl: snapshot.get(messageId)?.previewUrl ?? URL.createObjectURL(prepared.blob),
  });
  try {
    await runUpload(context.convoId, context.senderId, messageId, prepared);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('[talk2nIcK] 미디어 재업로드 실패:', error);
    setUpload(messageId, {
      ...(snapshot.get(messageId) ?? { progress: 0 }),
      status: 'failed',
      errorMessage: errorMsg,
    });
  }
}

/**
 * 업로드가 끝났는데 URL이 없는 문서(예: 이전 버전)를 위한 폴백.
 * 정상 경로에서는 `media.url`이 문서에 들어 있어 읽기가 0회다.
 */
const urlCache = new Map<string, string>();

export async function resolveMediaUrl(path: string): Promise<string | undefined> {
  const hit = urlCache.get(path);
  if (hit) return hit;
  try {
    const [{ getDownloadURL, ref: storageRef }, storage] = await Promise.all([
      import('firebase/storage'),
      getStorageClient(),
    ]);
    const url = await getDownloadURL(storageRef(storage, path));
    urlCache.set(path, url);
    return url;
  } catch (error) {
    console.error('[talk2nIcK] 미디어 URL 해석 실패:', error);
    return undefined;
  }
}
